import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "../api/client";
import "./Manufacture.css";

const STATUS_META = {
  returned_mid_transit: "Returned Mid-Transit",
  qa_check: "QA Check",
  to_clean: "To Clean",
  cleaned: "Cleaned",
  repair_needed: "Repair Needed",
  in_repair: "In Repair",
  repaired: "Repaired",
  repack_pending: "Repack Pending",
  repacked: "Repacked",
  ready_for_storage: "Ready for Storage",
  total_loss: "Total Loss",
};

const DEFAULT_FILTERS = {
  search: "",
  status: "all",
  minValue: "",
  maxValue: "",
};

const STATUS_VALUE_STORAGE_KEY = "manufacture_status_value_percentages";
const FILTERED_VALUE_STORAGE_KEY = "manufacture_filtered_value_total";
const FILTERED_VALUE_EVENT = "manufacture-filtered-value-updated";

const DEFAULT_STATUS_VALUES = Object.entries(STATUS_META).map(([status_key, status_label]) => ({
  status_key,
  status_label,
  value_percent:
    status_key === "total_loss"
      ? 0
      : ["repair_needed", "in_repair"].includes(status_key)
      ? 60
      : ["to_clean", "repack_pending"].includes(status_key)
      ? 80
      : ["cleaned", "repaired", "repacked"].includes(status_key)
      ? 90
      : 100,
}));

const loadStoredStatusValues = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(STATUS_VALUE_STORAGE_KEY) || "[]");
    return Array.isArray(parsed) && parsed.length ? parsed : DEFAULT_STATUS_VALUES;
  } catch {
    return DEFAULT_STATUS_VALUES;
  }
};

const formatMoney = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const normalizeInventoryRows = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.inventory)) return data.inventory;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

export default function ManufactureValueSetup() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [statusValues, setStatusValues] = useState(loadStoredStatusValues);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [itemsRes, valuesRes, inventoryRes] = await Promise.allSettled([
        client.get("/manufacture"),
        client.get("/manufacture/status-values"),
        client.get("/inventory"),
      ]);

      setItems(itemsRes.status === "fulfilled" && Array.isArray(itemsRes.value.data) ? itemsRes.value.data : []);
      if (valuesRes.status === "fulfilled" && Array.isArray(valuesRes.value.data)) {
        setStatusValues(valuesRes.value.data);
        localStorage.setItem(STATUS_VALUE_STORAGE_KEY, JSON.stringify(valuesRes.value.data));
      } else {
        setStatusValues(loadStoredStatusValues());
      }
      setInventory(
        inventoryRes.status === "fulfilled" ? normalizeInventoryRows(inventoryRes.value.data) : []
      );
    } catch (error) {
      console.error("Manufacture value setup load error:", error);
      alert("Failed to load manufacture value setup");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const inventoryPriceByName = useMemo(() => {
    const map = new Map();
    inventory.forEach((item) => {
      const name = String(item.name || item.product || "").trim().toLowerCase();
      if (!name) return;
      map.set(name, Number(item.price || item.cost_price || 0));
    });
    return map;
  }, [inventory]);

  const percentByStatus = useMemo(() => {
    const map = new Map();
    statusValues.forEach((row) => {
      map.set(row.status_key, Number(row.value_percent || 0));
    });
    return map;
  }, [statusValues]);

  const valuationRows = useMemo(() => {
    return items.map((item) => {
      const qty = Number(item.qty || 0);
      const inventoryUnitPrice =
        inventoryPriceByName.get(String(item.product || "").trim().toLowerCase()) ??
        Number(item.base_price || 0);
      const valuePercent = percentByStatus.get(item.status) ?? 100;
      const unitValue = Number(((inventoryUnitPrice * valuePercent) / 100).toFixed(2));
      const totalValue = Number((unitValue * qty).toFixed(2));

      return {
        ...item,
        qty,
        inventoryUnitPrice,
        valuePercent,
        unitValue,
        totalValue,
      };
    });
  }, [items, inventoryPriceByName, percentByStatus]);

  const filteredRows = useMemo(() => {
    const search = filters.search.trim().toLowerCase();
    const minValue = filters.minValue === "" ? null : Number(filters.minValue);
    const maxValue = filters.maxValue === "" ? null : Number(filters.maxValue);

    return valuationRows.filter((row) => {
      if (filters.status !== "all" && row.status !== filters.status) return false;
      if (search) {
        const text = [row.orderid, row.product, STATUS_META[row.status] || row.status]
          .join(" ")
          .toLowerCase();
        if (!text.includes(search)) return false;
      }
      if (minValue !== null && row.totalValue < minValue) return false;
      if (maxValue !== null && row.totalValue > maxValue) return false;
      return true;
    });
  }, [valuationRows, filters]);

  const totals = useMemo(
    () =>
      filteredRows.reduce(
        (acc, row) => ({
          units: acc.units + Number(row.qty || 0),
          value: acc.value + Number(row.totalValue || 0),
        }),
        { units: 0, value: 0 }
      ),
    [filteredRows]
  );

  useEffect(() => {
    const payload = {
      value: Number(totals.value || 0),
      units: Number(totals.units || 0),
      updatedAt: new Date().toISOString(),
    };
    localStorage.setItem(FILTERED_VALUE_STORAGE_KEY, JSON.stringify(payload));
    window.dispatchEvent(new CustomEvent(FILTERED_VALUE_EVENT, { detail: payload }));
  }, [totals.value, totals.units]);

  const updatePercent = (statusKey, value) => {
    setStatusValues((prev) =>
      prev.map((row) =>
        row.status_key === statusKey
          ? {
              ...row,
              value_percent:
                value === "" ? "" : Math.max(0, Math.min(100, Number(value || 0))),
            }
          : row
      )
    );
  };

  const saveStatusValues = async () => {
    try {
      setSaving(true);
      localStorage.setItem(STATUS_VALUE_STORAGE_KEY, JSON.stringify(statusValues));
      const { data } = await client.put("/manufacture/status-values", { values: statusValues });
      if (Array.isArray(data)) {
        setStatusValues(data);
        localStorage.setItem(STATUS_VALUE_STORAGE_KEY, JSON.stringify(data));
      }
      alert("Value percentages saved");
    } catch (error) {
      console.error("Manufacture status value save error:", error);
      localStorage.setItem(STATUS_VALUE_STORAGE_KEY, JSON.stringify(statusValues));
      alert("Value percentages saved");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="manufacturing-container manufacture-value-page page-container">
      <div className="page-head">
        <h2>Manufacture Value Setup</h2>
        <div className="manufacture-top-actions">
          <button className="btn outline" type="button" onClick={() => navigate("/manufacture")}>
            Back
          </button>
          <button className="btn outline" type="button" onClick={fetchData} disabled={loading}>
            {loading ? "Loading..." : "Refresh"}
          </button>
          <button className="btn primary" type="button" onClick={saveStatusValues} disabled={saving}>
            {saving ? "Saving..." : "Save Percentages"}
          </button>
        </div>
      </div>

      <div className="manufacture-value-rule-card">
        <div className="manufacture-value-rule-head">
          <h3>Status Value Percentages</h3>
          <span>Inventory price x percentage</span>
        </div>
        <div className="manufacture-value-rule-grid">
          {statusValues.map((row) => (
            <label key={row.status_key} className="manufacture-value-rule">
              <span>{row.status_label || STATUS_META[row.status_key] || row.status_key}</span>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={row.value_percent}
                onChange={(event) => updatePercent(row.status_key, event.target.value)}
              />
            </label>
          ))}
        </div>
      </div>

      <div className="manufacture-filters-card">
        <div className="manufacture-value-filters-grid">
          <input
            className="filter-input"
            placeholder="Search order or product"
            value={filters.search}
            onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
          />
          <select
            className="filter-input"
            value={filters.status}
            onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))}
          >
            <option value="all">All Status</option>
            {Object.entries(STATUS_META).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
          <input
            className="filter-input"
            type="number"
            min="0"
            placeholder="Min total value"
            value={filters.minValue}
            onChange={(event) => setFilters((prev) => ({ ...prev, minValue: event.target.value }))}
          />
          <input
            className="filter-input"
            type="number"
            min="0"
            placeholder="Max total value"
            value={filters.maxValue}
            onChange={(event) => setFilters((prev) => ({ ...prev, maxValue: event.target.value }))}
          />
          <button className="btn outline" type="button" onClick={() => setFilters(DEFAULT_FILTERS)}>
            Clear Filters
          </button>
        </div>
      </div>

      <div className="manufacture-value-summary">
        <div>
          <span>Filtered Units</span>
          <strong>{formatMoney(totals.units)}</strong>
        </div>
        <div>
          <span>Filtered Value</span>
          <strong>Rs.{formatMoney(totals.value)}</strong>
        </div>
      </div>

      <div className="table-card">
        <table className="manufacturing-table manufacture-value-table">
          <thead>
            <tr>
              <th>Order</th>
              <th>Product</th>
              <th>Status</th>
              <th>Qty</th>
              <th>Inventory Unit Price</th>
              <th>Value %</th>
              <th>Per Unit Price</th>
              <th>Total Unit Price</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" style={{ textAlign: "center", padding: 18 }}>
                  Loading...
                </td>
              </tr>
            ) : filteredRows.length === 0 ? (
              <tr>
                <td colSpan="8" style={{ textAlign: "center", padding: 18 }}>
                  No matching items
                </td>
              </tr>
            ) : (
              filteredRows.map((row) => (
                <tr key={row.id}>
                  <td>{row.orderid || "-"}</td>
                  <td>{row.product || "-"}</td>
                  <td>{STATUS_META[row.status] || row.status || "-"}</td>
                  <td>{formatMoney(row.qty)}</td>
                  <td>Rs.{formatMoney(row.inventoryUnitPrice)}</td>
                  <td>{formatMoney(row.valuePercent)}%</td>
                  <td>Rs.{formatMoney(row.unitValue)}</td>
                  <td>
                    <strong>Rs.{formatMoney(row.totalValue)}</strong>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
